import { Inject, Injectable } from '@angular/core';
import { ClassesEntity } from '../../models/entities/classes-entity';
import { ClassVmCostReport, IClassesEntityService } from '../../interfaces/entities/classes-entity-service.interface';
import { COURSE_ENTITY_SERVICE_TOKEN, CUSTOMER_ENTITY_SERVICE_TOKEN } from '../../injection-tokens/entity-service-tokens';
import { ICustomerEntityService } from '../../interfaces/entities/customer-entity-service.interface';
import { ICourseEntityService } from '../../interfaces/entities/course-entity-service.interface';
import { TrainingSchedule } from '../../models/entities/requisition-model';
import { BaseEntityService } from './base-entity.service';
import { CustomerEntity } from '../../models/entities/customer';
import { ContactEntity } from '../../models/entities/contact-entity';
import { CourseEntity } from '../../models/entities/course';
import { TrainerEntity } from '../../models/entities/trainer';
import { CourseVMImage } from '../../models/entities/course-vm-image';
import { LabEntity } from '../../models/entities/lab-entity';
import { Observable, of, throwError } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { HttpClient } from '@angular/common/http';
import { Log, IShortLogger } from '../logger.service';

/** Response shape from GET /api/v1/user-vm/status and POST /api/v1/user-vm/prepare. */
export interface UserVmStatusResponse {
  state: UserVmProvisionState;
  hostname?: string;
  readyAt?: string;
  errorMessage?: string;
  estimatedSeconds?: number;
}

export type UserVmProvisionState =
  | 'not_created'
  | 'creating'
  | 'ready'
  | 'failed'
  | 'deleting'
  | null;

@Injectable({
  providedIn: 'root'
})
export class ClassEntityService extends BaseEntityService<ClassesEntity> implements IClassesEntityService {
  private readonly logger: IShortLogger<ClassEntityService> = Log.create(ClassEntityService);

  protected override initializeData(): void {
    // JSON data converted to ClassesEntity objects
    const jsonData = [
      {
        "id": "1602a99c-a81a-48ef-a699-3ad927004478",
        "name": "Azure Fundamentals",
        "isActive": true,
        "createdBy": "system",
        "createdOn": "2025-06-04T13:53:34.944Z",
        "updatedOn": "2025-06-04T13:53:34.944Z",
        "updatedBy": "system",
        "requisitionId": "",
        "customer": {
          "id": "6ba7b811-9dad-11d1-80b4-00c04fd430cb",
          "name": "Innovation Corp",
          "isActive": true,
          "createdBy": "system",
          "createdOn": "2024-02-05T00:00:00.000Z",
          "updatedOn": "2024-02-05T00:00:00.000Z",
          "updatedBy": "system",
          "address": "789 Tech Valley, Austin, TX 78701",
          "website": "https://www.innovationcorp.com",
          "gstNumber": "44MNOPQ1234R6S7",
          "contactPerson": {
            "id": "6ba7b811-9dad-11d1-80b4-00c04fd430cc",
            "name": "Emily Davis",
            "isActive": true,
            "createdBy": "system",
            "createdOn": "2025-06-04T13:51:34.470Z",
            "updatedOn": "2025-06-04T13:51:34.470Z",
            "updatedBy": "system",
            "email": "emily.davis@innovationcorp.com",
            "mobile": 5555559999
          }
        },
        "trainingSchedule": {
          "id": "496c0de0-3c47-44b6-b20e-23e3e8ab5b69",
          "name": "",
          "isActive": true,
          "createdBy": "system",
          "createdOn": "2025-06-04T13:53:34.943Z",
          "updatedOn": "2025-06-04T13:53:34.943Z",
          "updatedBy": "system",
          "reqId": "",
          "srNo": 1,
          "title": "Training Schedule 1",
          "startDate": "2025-06-04T00:00:00.000Z",
          "endDate": "2025-06-11T00:00:00.000Z",
          "startTime": "20:22",
          "endTime": "23:27",
          "offDates": [],
          "participants": [],
          "resourceTemplateIds": [],
          "course": {
            "id": "550e8400-e29b-41d4-a716-446655440010",
            "name": "Azure Fundamentals",
            "isActive": true,
            "createdBy": "system",
            "createdOn": "2024-01-15T00:00:00.000Z",
            "updatedOn": "2024-01-15T00:00:00.000Z",
            "updatedBy": "system",
            "code": "AZ-900",
            "provider": "Microsoft",
            "description": "Learn the basics of Azure cloud services, core solutions, and management tools.",
            "vmImage": {
              "cloudProviderData": {
                "provider": "azure",
                "region": "centralindia",
                "size": "Standard_B1s",
                "storage": "Standard_LRS"
              },
              "softwareData": {
                "os": "windows",
                "osVersion": "win10",
                "sdks": [],
                "databases": [],
                "editors": [],
                "misc": ["chrome"]
              }
            },
            "labs": [
              {
                "id": "550e8400-e29b-41d4-a716-446655440011",
                "name": "Introduction to Azure",
                "isActive": true,
                "createdBy": "system",
                "createdOn": "2024-01-15T00:00:00.000Z",
                "updatedOn": "2024-01-15T00:00:00.000Z",
                "updatedBy": "system",
                "code": "LAB01",
                "labOrder": 1,
                "description": "Getting familiar with Azure portal and basic services",
                "durationInHours": 2,
                "hasHandsOn": true,
                "labContents": [
                  "Azure Portal Overview: The Azure Portal is a web-based interface that lets users manage and monitor Azure resources through a unified dashboard. It offers tools for configuring services, tracking usage, and managing security, making cloud management easier and more efficient.",
                  "Creating Resource Groups:Creating Resource Groups in Azure involves organizing related resources like virtual machines, databases, and storage into a single container. This helps manage, monitor, and apply permissions or policies efficiently across grouped services.",
                  "Basic Virtual Machines:Basic Virtual Machines in Azure provide cost-effective compute options for development, testing, and light workloads. They offer essential features with lower pricing, making them ideal for non-critical applications and entry-level tasks."
                ],
                "labContentsUrl": "https://docs.microsoft.com/azure/fundamentals/lab01"
              },
              {
                "id": "550e8400-e29b-41d4-a716-446655440012",
                "name": "Azure Storage",
                "isActive": true,
                "createdBy": "system",
                "createdOn": "2024-01-15T00:00:00.000Z",
                "updatedOn": "2024-01-15T00:00:00.000Z",
                "updatedBy": "system",
                "code": "LAB02",
                "labOrder": 2,
                "description": "Exploring different storage options in Azure",
                "durationInHours": 3,
                "hasHandsOn": true,
                "labContents": ["Blob Storage", "File Storage", "Storage Accounts"],
                "labContentsUrl": "https://docs.microsoft.com/azure/fundamentals/lab02"
              }
            ]
          },
          "trainers": [
            {
              "id": "6ba7b810-9dad-11d1-80b4-00c04fd430c8",
              "name": "Sarah Johnson",
              "isActive": true,
              "createdBy": "system",
              "createdOn": "2024-01-08T00:00:00.000Z",
              "updatedOn": "2024-01-08T00:00:00.000Z",
              "updatedBy": "system",
              "email": "sarah.johnson@example.com",
              "mobile": 9876543210,
              "age": 28,
              "skills": [".NET", "C#", "Angular", "SQL Server"],
              "bioData": "Sarah is a full-stack developer with expertise in Microsoft technologies. She has trained hundreds of developers."
            }
          ]
        }
      },
      {
        "id": "a0777f9d-36dd-4918-94e1-a373c941985f",
        "name": "Training Schedule 2 (Custom Course)",
        "isActive": true,
        "createdBy": "system",
        "createdOn": "2025-06-04T13:53:34.944Z",
        "updatedOn": "2025-06-04T13:53:34.944Z",
        "updatedBy": "system",
        "requisitionId": "",
        "customer": {
          "id": "6ba7b811-9dad-11d1-80b4-00c04fd430cb",
          "name": "Innovation Corp",
          "isActive": true,
          "createdBy": "system",
          "createdOn": "2024-02-05T00:00:00.000Z",
          "updatedOn": "2024-02-05T00:00:00.000Z",
          "updatedBy": "system",
          "address": "789 Tech Valley, Austin, TX 78701",
          "website": "https://www.innovationcorp.com",
          "gstNumber": "44MNOPQ1234R6S7",
          "contactPerson": {
            "id": "6ba7b811-9dad-11d1-80b4-00c04fd430cc",
            "name": "Emily Davis",
            "isActive": true,
            "createdBy": "system",
            "createdOn": "2025-06-04T13:51:34.470Z",
            "updatedOn": "2025-06-04T13:51:34.470Z",
            "updatedBy": "system",
            "email": "emily.davis@innovationcorp.com",
            "mobile": 5555559999
          }
        },
        "trainingSchedule": {
          "id": "b8abf7f5-53ef-40c7-aeb6-4b633a08d5eb",
          "name": "",
          "isActive": true,
          "createdBy": "system",
          "createdOn": "2025-06-04T13:53:34.943Z",
          "updatedOn": "2025-06-04T13:53:34.943Z",
          "updatedBy": "system",
          "reqId": "",
          "srNo": 2,
          "title": "Training Schedule 2",
          "startDate": "2025-06-04T00:00:00.000Z",
          "endDate": "2025-06-04T00:00:00.000Z",
          "startTime": "21:23",
          "endTime": "23:23",
          "offDates": [],
          "participants": [],
          "resourceTemplateIds": [],
          "trainers": [
            {
              "id": "550e8400-e29b-41d4-a716-446655440001",
              "name": "John Smith",
              "isActive": true,
              "createdBy": "system",
              "createdOn": "2024-01-05T00:00:00.000Z",
              "updatedOn": "2024-01-05T00:00:00.000Z",
              "updatedBy": "system",
              "email": "john.smith@example.com",
              "mobile": 1234567890,
              "age": 35,
              "skills": ["Azure", "AWS", "Docker", "Kubernetes"],
              "bioData": "John has 10+ years of experience in cloud technologies and DevOps. He specializes in Azure and AWS solutions."
            }
          ]
        }
      }
    ];

    this.entities = jsonData.map(item => this.convertJsonToClassEntity(item));
  }

  private convertJsonToClassEntity(jsonItem: any): ClassesEntity {
    // Create customer entity
    const customer = new CustomerEntity();
    customer.id = jsonItem.customer.id;
    customer.name = jsonItem.customer.name;
    customer.isActive = jsonItem.customer.isActive;
    customer.createdBy = jsonItem.customer.createdBy;
    customer.createdOn = jsonItem.customer.createdOn ? new Date(jsonItem.customer.createdOn) : undefined;
    customer.updatedBy = jsonItem.customer.updatedBy;
    customer.updatedOn = jsonItem.customer.updatedOn ? new Date(jsonItem.customer.updatedOn) : undefined;
    customer.address = jsonItem.customer.address;
    customer.website = jsonItem.customer.website;
    customer.gstNumber = jsonItem.customer.gstNumber;

    // Create contact person
    const contactPerson = new ContactEntity();
    contactPerson.id = jsonItem.customer.contactPerson.id;
    contactPerson.name = jsonItem.customer.contactPerson.name;
    contactPerson.isActive = jsonItem.customer.contactPerson.isActive;
    contactPerson.createdBy = jsonItem.customer.contactPerson.createdBy;
    contactPerson.createdOn = jsonItem.customer.contactPerson.createdOn ? new Date(jsonItem.customer.contactPerson.createdOn) : undefined;
    contactPerson.updatedBy = jsonItem.customer.contactPerson.updatedBy;
    contactPerson.updatedOn = jsonItem.customer.contactPerson.updatedOn ? new Date(jsonItem.customer.contactPerson.updatedOn) : undefined;
    contactPerson.email = jsonItem.customer.contactPerson.email;
    contactPerson.mobile = jsonItem.customer.contactPerson.mobile;

    customer.contactPerson = contactPerson;

    // Create training schedule
    const trainingSchedule = new TrainingSchedule();
    trainingSchedule.id = jsonItem.trainingSchedule.id;
    trainingSchedule.name = jsonItem.trainingSchedule.name || '';
    trainingSchedule.isActive = jsonItem.trainingSchedule.isActive;
    trainingSchedule.createdBy = jsonItem.trainingSchedule.createdBy;
    trainingSchedule.createdOn = jsonItem.trainingSchedule.createdOn ? new Date(jsonItem.trainingSchedule.createdOn) : undefined;
    trainingSchedule.updatedBy = jsonItem.trainingSchedule.updatedBy;
    trainingSchedule.updatedOn = jsonItem.trainingSchedule.updatedOn ? new Date(jsonItem.trainingSchedule.updatedOn) : undefined;
    trainingSchedule.reqId = jsonItem.trainingSchedule.reqId;
    trainingSchedule.srNo = jsonItem.trainingSchedule.srNo;
    trainingSchedule.title = jsonItem.trainingSchedule.title;
    trainingSchedule.startDate = new Date(jsonItem.trainingSchedule.startDate);
    trainingSchedule.endDate = new Date(jsonItem.trainingSchedule.endDate);
    trainingSchedule.startTime = jsonItem.trainingSchedule.startTime;
    trainingSchedule.endTime = jsonItem.trainingSchedule.endTime;
    trainingSchedule.offDates = jsonItem.trainingSchedule.offDates.map((date: string) => new Date(date));
    trainingSchedule.participants = [...jsonItem.trainingSchedule.participants];
    trainingSchedule.resourceTemplateIds = [...jsonItem.trainingSchedule.resourceTemplateIds];

    // Create trainers
    trainingSchedule.trainers = jsonItem.trainingSchedule.trainers.map((trainerData: any) => {
      const trainer = new TrainerEntity();
      trainer.id = trainerData.id;
      trainer.name = trainerData.name;
      trainer.isActive = trainerData.isActive;
      trainer.createdBy = trainerData.createdBy;
      trainer.createdOn = trainerData.createdOn ? new Date(trainerData.createdOn) : undefined;
      trainer.updatedBy = trainerData.updatedBy;
      trainer.updatedOn = trainerData.updatedOn ? new Date(trainerData.updatedOn) : undefined;
      trainer.email = trainerData.email;
      trainer.mobile = trainerData.mobile;
      trainer.age = trainerData.age;
      trainer.skills = [...trainerData.skills];
      trainer.bioData = trainerData.bioData;
      return trainer;
    });

    // Create course if it exists (for non-custom courses)
    if (jsonItem.trainingSchedule.course) {
      const courseData = jsonItem.trainingSchedule.course;
      const course = new CourseEntity();
      course.id = courseData.id;
      course.name = courseData.name;
      course.isActive = courseData.isActive;
      course.createdBy = courseData.createdBy;
      course.createdOn = courseData.createdOn ? new Date(courseData.createdOn) : undefined;
      course.updatedBy = courseData.updatedBy;
      course.updatedOn = courseData.updatedOn ? new Date(courseData.updatedOn) : undefined;
      course.code = courseData.code;
      course.provider = courseData.provider;
      course.description = courseData.description;

      // Create VM Image
      const vmImage = new CourseVMImage();
      vmImage.cloudProviderData.provider = courseData.vmImage.cloudProviderData.provider;
      vmImage.cloudProviderData.region = courseData.vmImage.cloudProviderData.region;
      vmImage.cloudProviderData.size = courseData.vmImage.cloudProviderData.size;
      vmImage.cloudProviderData.storage = courseData.vmImage.cloudProviderData.storage;
      vmImage.softwareData.os = courseData.vmImage.softwareData.os;
      vmImage.softwareData.osVersion = courseData.vmImage.softwareData.osVersion;
      vmImage.softwareData.sdks = [...courseData.vmImage.softwareData.sdks];
      vmImage.softwareData.databases = [...courseData.vmImage.softwareData.databases];
      vmImage.softwareData.editors = [...courseData.vmImage.softwareData.editors];
      vmImage.softwareData.misc = [...courseData.vmImage.softwareData.misc];
      course.vmImage = vmImage;

      // Create labs
      course.labs = courseData.labs.map((labData: any) => {
        const lab = new LabEntity();
        lab.id = labData.id;
        lab.name = labData.name;
        lab.isActive = labData.isActive;
        lab.createdBy = labData.createdBy;
        lab.createdOn = labData.createdOn ? new Date(labData.createdOn) : undefined;
        lab.updatedBy = labData.updatedBy;
        lab.updatedOn = labData.updatedOn ? new Date(labData.updatedOn) : undefined;
        lab.code = labData.code;
        lab.labOrder = labData.labOrder;
        lab.description = labData.description;
        lab.durationInHours = labData.durationInHours;
        lab.hasHandsOn = labData.hasHandsOn;
        lab.labContents = [...labData.labContents];
        lab.labContentsUrl = labData.labContentsUrl;
        return lab;
      });

      trainingSchedule.course = course;
    }

    // Create the ClassesEntity
    const classEntity = new ClassesEntity(trainingSchedule);
    classEntity.id = jsonItem.id;
    classEntity.name = jsonItem.name;
    classEntity.isActive = jsonItem.isActive;
    classEntity.createdBy = jsonItem.createdBy;
    classEntity.createdOn = jsonItem.createdOn ? new Date(jsonItem.createdOn) : undefined;
    classEntity.updatedBy = jsonItem.updatedBy;
    classEntity.updatedOn = jsonItem.updatedOn ? new Date(jsonItem.updatedOn) : undefined;
    classEntity.requisitionId = jsonItem.requisitionId;
    classEntity.customer = customer;

    return classEntity;
  }

  override createEntity(entity: ClassesEntity, createdBy: string): ClassesEntity {
    const newEntity = new ClassesEntity(entity.trainingSchedule);
    this.setCreateAuditProperties(newEntity, createdBy);

    newEntity.requisitionId = entity.requisitionId;
    newEntity.customer = entity.customer;
    newEntity.course = entity.course;

    this.entities.push(newEntity);
    return newEntity;
  }

  override updateEntity(id: string, updates: Partial<ClassesEntity>, updatedBy: string): ClassesEntity | null {
    const entityIndex = this.entities.findIndex(entity => entity.id === id);

    if (entityIndex === -1) {
      return null;
    }

    const entity = this.entities[entityIndex];
    this.setUpdateAuditProperties(entity, updatedBy);

    // Update properties if provided
    if (updates.name !== undefined) entity.name = updates.name;
    if (updates.requisitionId !== undefined) entity.requisitionId = updates.requisitionId;
    if (updates.customer !== undefined) entity.customer = updates.customer;
    if (updates.trainingSchedule !== undefined) entity.trainingSchedule = updates.trainingSchedule;
    if (updates.course !== undefined) entity.course = updates.course;

    return entity;
  }

  constructor(
    @Inject(CUSTOMER_ENTITY_SERVICE_TOKEN) private customerService: ICustomerEntityService,
    @Inject(COURSE_ENTITY_SERVICE_TOKEN) private courseService: ICourseEntityService,
    protected override http: HttpClient
  ) {
    super();
  }

  getAllClasses(): ClassesEntity[] {
    this.loadClassesFromLocalStorage();
    return [...this.entities];
  }

  getClassById(id: string): ClassesEntity | undefined {
    return this.entities.find(cls => cls.id === id);
  }

  createClass(trainingSchedule: TrainingSchedule, customerId: string): ClassesEntity {
    const classEntity = new ClassesEntity(trainingSchedule);
    this.setCreateAuditProperties(classEntity, trainingSchedule.updatedBy);
    classEntity.customer = this.customerService.getCustomerById(customerId)!;
    this.entities.push(classEntity);
    this.saveClassesToLocalStorage();
    return classEntity;
  }

  getClassesByStatus(status: 'OnGoing' | 'Upcoming' | 'Completed'): ClassesEntity[] {
    return this.entities.filter(cls => cls.status === status);
  }

  getClassesByCustomerId(customerId: string): ClassesEntity[] {
    return this.entities.filter(cls => cls.customer?.id === customerId);
  }
  public saveClassesToLocalStorage(): void {
    try {
      const jsonData = this.entities.map(entity => JSON.parse(JSON.stringify(entity)));
      localStorage.setItem('classList', JSON.stringify(jsonData));
    } catch (error) {
      this.logger.e('Error saving class list to localStorage:', error);
    }
  }

  public loadClassesFromLocalStorage(): void {
    try {
      const data = localStorage.getItem('classList');
      if (!data) return;

      const parsed = JSON.parse(data);
      this.entities = parsed.map((json: any) => this.convertJsonToClassEntity(json));
    } catch (error) {
      this.logger.e('Error loading class list from localStorage:', error);
    }
  }

  public classGetByUserId(userId: string): Observable<any> {
    return this.http.get(`${this.serviceUrl}/v1/classes/by-user/${userId}`);
  }

  public getLaunchDetails(classId: string, userId: string, role: string, userName: string, context: string): Observable<any> {
    const url = `${this.serviceUrl}/v1/classes/launch-details/${classId}/${userId}/${role}/${userName}/${context}`;
    // TODO: INTERCEPTOR-LOGGING - Remove HTTP request logging, handle via request-response interceptor
    this.logger.d('🚀 Calling backend API:', {
      url: url,
      classId: classId,
      userId: userId,
      role: role,
      userName: userName,
      description: `Getting launch details for ${userName} (${userId}) in class ${classId} with role ${role}`
      , context: context
    });

    return this.http.get(url).pipe(
      tap(response => {
        // TODO: INTERCEPTOR-LOGGING - Remove HTTP response logging, handle via request-response interceptor
        this.logger.d('📥 Backend API Response:', {
          url: url,
          requestedUserId: userId,
          requestedUserName: userName,
          response: response,
          responseType: 'launch-details'
        });
      })
    );
  }

  public getTemplateLaunchDetails(templateId: string, classId?: string): Observable<any> {
    const options = classId ? { params: { classId } } : {};
    return this.http.get(`${this.serviceUrl}/v1/classes/template-launch-details/${templateId}`, options);
  }

  public getTemplateStatus(templateId: string, customerId?: string, classId?: string): Observable<any> {
    const queryParams: Record<string, string> = {};
    if (customerId) queryParams['customerId'] = customerId;
    if (classId) queryParams['classId'] = classId;
    const params = Object.keys(queryParams).length ? { params: queryParams } : {};
    return this.http.get(`${this.serviceUrl}/v1/classes/template-status/${templateId}`, params);
  }
  /**
   * Reads the cloud user account credentials for a participant.
   * @param autoProvision When true, triggers on-demand provisioning for NotStarted / absent
   *   accounts. Defaults to false so a plain page-load status check never side-effects provisioning.
   */
  public getCloudUserAccount(classId: string, userId: string, autoProvision = false): Observable<any> {
    return this.http.get(
      `${this.serviceUrl}/v1/classes/cloudUserAccount/${classId}/${userId}`,
      { params: { autoProvision: String(autoProvision) } },
    );
  }

  /**
   * Regenerates the Temporary Access Pass for a participant's cloud user account.
   * Calls POST /api/v1/classes/{classId}/ua-subscriptions/{participantId}/regenerate-tap.
   */
  public regenerateParticipantTap(classId: string, participantId: string): Observable<{ temporaryAccessPass: string }> {
    return this.http.post<{ temporaryAccessPass: string }>(
      `${this.serviceUrl}/v1/classes/${classId}/ua-subscriptions/${participantId}/regenerate-tap`, {});
  }

  public getActiveSessionsByClassId(classId: string): Observable<any> {
    return this.http.get(`${this.serviceUrl}/v1/classes/active-sessions/${classId}`);
  }

  public upsertSessionThumbnail(dto: { classId: string; userId: string; context: string; thumbnailData: string }): Observable<void> {
    return this.http.patch<void>(`${this.serviceUrl}/v1/classes/sessions/thumbnail`, dto);
  }
  public getCostingData(classId: string): Observable<any> {
    return this.http.get(`${this.serviceUrl}/v1/classes/costing/${classId}/true`);
  }

  public getClassVmCostReport(classId: string): Observable<ClassVmCostReport> {
    return this.http.get<ClassVmCostReport>(`${this.serviceUrl}/v1/classes/${classId}/vm-cost-report`);
  }

  /**
   * Streams a styled `.xlsx` workbook for the class VM cost report from the backend.
   * Returns the raw binary as a {@link Blob} so the caller can trigger a browser download.
   */
  public exportClassVmCostReport(classId: string): Observable<Blob> {
    return this.http.get(
      `${this.serviceUrl}/v1/classes/${classId}/vm-cost-report/export`,
      { responseType: 'blob' }
    );
  }

  public classGetByredemptionKey(redemptionKey: string): Observable<any> {
    return this.http.get(`${this.serviceUrl}/v1/classes/by-redemption-key/${redemptionKey}`);
    // http://localhost:5001/api/v1/classes/by-redemption-key/c62ad8a6-ca46-42d1-b87a-e6686b6d82f7
  }
  public getClassResources(classId: string): Observable<any> {
    // private apiUrl = 'http://localhost:5001/api/v1/classes/resources/6895b6f83b5e35ab4b59ca17'; do changes accordingly
    return this.http.get<any>(`${this.serviceUrl}/v1/classes/resources/${classId}`);
  }

  /**
   * Creates a snapshot from a specific template ID.
   * `classId` should always be passed when known — it lets the backend resolve this class's own
   * TenantSecretId instead of guessing a tenant from the template's shared CustomerCloudIds map
   * (unsafe once a template is shared across classes on different tenants for the same customer).
   */
  public createSnapshot(templateId: string, classId?: string): Observable<any> {
    const headers = { "content-type": "application/json" };
    const url = classId
      ? `${this.serviceUrl}/v1/classes/snapshot/${templateId}?classId=${encodeURIComponent(classId)}`
      : `${this.serviceUrl}/v1/classes/snapshot/${templateId}`;
    return this.http.put(url, {}, { headers });
  }

  updateRedemptionStatus(redemptionKey: string, data: { [key: string]: any }) {

    const objRedemptionData = {
      redemptionKey: redemptionKey,
      data: data
    };
    // TODO: INTERCEPTOR-LOGGING - Remove HTTP request body logging, handle via request-response interceptor
    this.logger.d('updateRedemptionStatus objRedemptionData:', objRedemptionData);
    const headers = { "content-type": "application/json" };
    const body = JSON.stringify(objRedemptionData);

    return this.http.put(`${this.serviceUrl}/v1/classes/update-redemption-status`, body, { headers });
  }



  updateClassPartial(classId: string, data: { [key: string]: any }): Observable<any> {
    // TODO: INTERCEPTOR-LOGGING - Remove HTTP request data logging, handle via request-response interceptor
    this.logger.d('updateClassPartial:', data);
    const headers = { "content-type": "application/json" };
    const body = JSON.stringify(data);

    return this.http.put(`${this.serviceUrl}/v1/classes/updateClassPartial/${classId}`, body, { headers });

  }

  /**
   * Updates the editable fields of a class training schedule (DateRanges, Title, TrainingDetail,
   * TrainersRemarks, AccessViaInvitedEmailOnly, RemovalOption, RemovalDateTime) and syncs the
   * changes back to the originating requisition.
   *
   * ResourceTemplateIds are **not** accepted by this endpoint — they are locked once the class is
   * active. Participants and Trainers continue to use `updateClassPartial`.
   */
  updateTrainingSchedule(classId: string, request: any): Observable<any> {
    this.logger.d('updateTrainingSchedule:', request);
    const headers = { "content-type": "application/json" };
    const body = JSON.stringify(request);
    return this.http.put(`${this.serviceUrl}/v1/classes/${classId}/training-schedule`, body, { headers });
  }

  /**
   * Returns per-subscription Azure quota data for all VM subscriptions assigned to a class.
   * Data comes from the MongoDB quota cache — no live Azure API calls are triggered.
   * Maps to GET /api/v1/classes/{classId}/subscription-quota-summary.
   */
  getSubscriptionQuotaSummary(classId: string): Observable<any[]> {
    return this.http.get<any[]>(`${this.serviceUrl}/v1/classes/${classId}/subscription-quota-summary`);
  }

  public destroyVM(classId: string, userId: string, destroyOSDisk: boolean = false): Observable<any> {
    return this.http.delete(
      `${this.serviceUrl}/v1/classes/destroy-vm/${classId}/${userId}/${destroyOSDisk}`
    );
  }

  public shutdownVM(classId: string, userId: string): Observable<any> {
    return this.http.post(`${this.serviceUrl}/v1/classes/shutdown-vm/${classId}/${userId}`, {});
  }

  public shutdownAllVMs(classId: string): Observable<any> {
    return this.http.post(`${this.serviceUrl}/v1/classes/shutdown-vm-all/${classId}`, {});
  }

  public deallocateVM(classId: string, userId: string): Observable<any> {
    return this.http.post(`${this.serviceUrl}/v1/classes/deallocate-vm/${classId}/${userId}`, {});
  }

  public deallocateAllVMs(classId: string): Observable<any> {
    return this.http.post(`${this.serviceUrl}/v1/classes/deallocate-vm-all/${classId}`, {});
  }

  public shutdownAndDeallocateVM(classId: string, userId: string): Observable<any> {
    return this.http.post(`${this.serviceUrl}/v1/classes/shutdown-deallocate-vm/${classId}/${userId}`, {});
  }

  public shutdownAndDeallocateAllVMs(classId: string): Observable<any> {
    return this.http.post(`${this.serviceUrl}/v1/classes/shutdown-deallocate-vm-all/${classId}`, {});
  }

  public startVM(classId: string, userId: string): Observable<any> {
    return this.http.post(`${this.serviceUrl}/v1/classes/start-vm/${classId}/${userId}`, {});
  }

  public startAllVMs(classId: string): Observable<any> {
    return this.http.post(`${this.serviceUrl}/v1/classes/start-vm-all/${classId}`, {});
  }

  public getVMPowerStates(classId: string): Observable<Record<string, string>> {
    return this.http.get<Record<string, string>>(`${this.serviceUrl}/v1/classes/vm-power-states/${classId}`);
  }

  public destroyUserAccount(userId: string, classId: string): Observable<any> {
    return this.http.delete(`${this.serviceUrl}/v1/classes/destroy-user-account/${userId}/${classId}`);
  }
  public destroyTemplate(templateId: string, classId: string, destroyOsDisk = false): Observable<any> {
    const query = destroyOsDisk ? '?destroyOsDisk=true' : '';
    return this.http.delete(`${this.serviceUrl}/v1/classes/destroy-template/${templateId}/${classId}${query}`);
  }

  /**
   * Fetches per-user Azure resource usage for a class's User Account subscriptions.
   * Maps to GET /api/v1/classes/user-account-usage/{classId}/{online}
   * @param online When true, fetches live data from Azure; when false uses cached data if available.
   */
  public getUserAccountUsageByClassId(classId: string, online: boolean = false): Observable<any> {
    return this.http.get(`${this.serviceUrl}/v1/classes/user-account-usage/${classId}/${online}`);
  }

  public getClassByClassId(classId: string): Observable<ClassesEntity> {
    return this.http.get<any>(`${this.serviceUrl}/v1/classes/by-class-id/${classId}`).pipe(
      map(r => this.normalizeClassResponse(r))
    );
  }

  /**
   * The API serialises the C# `CustomerDetails` property as `customerDetails` (camelCase),
   * but `ClassesEntity` declares the field as `customer`. Copy across so all callers that
   * access `.customer` receive the correct value without needing per-call workarounds.
   *
   * Also handles the case where the response already has a `customer` key but it is an
   * empty/incomplete object (no `id`). In that case `customerDetails` wins because it is
   * the authoritative raw MongoDB value.
   */
  private normalizeClassResponse(response: any): ClassesEntity {
    if (response?.customerDetails && (!response?.customer || !response?.customer?.id)) {
      response.customer = response.customerDetails;
    }
    return response as ClassesEntity;
  }

  public getClassTenantCredentials(classId: string): Observable<{
    isSpConfigured: boolean;
    tenantIdMasked: string;
    clientIdMasked: string;
    isClientSecretSet: boolean;
  }> {
    return this.http.get<any>(`${this.serviceUrl}/v1/classes/by-class-id/${classId}/tenant-credentials`);
  }

  public getClassByRequisitionId(requisitionId: string): Observable<ClassesEntity> {
    return this.http.get<any>(`${this.serviceUrl}/v1/classes/by-requisition-id/${requisitionId}`).pipe(
      map(r => this.normalizeClassResponse(r))
    );
  }

  public createTemplateVMForClass(classId: string): Observable<any> {
    return this.http.get<ClassesEntity>(`${this.serviceUrl}/v1/classes/create-template/${classId}`);
  }

  public getAllTemplateSubscription(): Observable<any> {
    return this.http.get(`${this.serviceUrl}/v1/classes/template-subscription/all`);
  }

  public lookupTemplateSubscription(
    subscriptionId: string
  ): Observable<{ success: boolean; subscriptionId: string; displayName: string }> {
    return this.http.get<{ success: boolean; subscriptionId: string; displayName: string }>(
      `${this.serviceUrl}/v1/classes/template-subscription/lookup/${encodeURIComponent(subscriptionId)}`
    );
  }


  createTemplateSubscription(templateSubscription: any): Observable<any> {

    const headers = { "content-type": "application/json" };
    const body = JSON.stringify(templateSubscription);

    // TODO: INTERCEPTOR-LOGGING - Remove HTTP request body logging, handle via request-response interceptor
    this.logger.d("Creating Template Subscription with body: ", body);
    let created = this.http.post(`${this.serviceUrl}/v1/classes/template-subscription`, body, { headers });
    return created;
  }

  public deleteTemplateSubscription(templateSubscriptionId: string): Observable<any> {
    return this.http.delete(`${this.serviceUrl}/v1/classes/template-subscription/${templateSubscriptionId}`);
  }

  /** Forces a live Azure quota refresh for a template subscription document and returns updated quota data. */
  public refreshTemplateSubscriptionQuota(id: string): Observable<any> {
    return this.http.post(
      `${this.serviceUrl}/v1/classes/template-subscription/${encodeURIComponent(id)}/refresh-quota`,
      {}
    );
  }

  updateTemplateSubscription(subscriptionId: string, templateSubscription: any): Observable<any> {
    const headers = { "content-type": "application/json" };
    const body = JSON.stringify(templateSubscription);

    // TODO: INTERCEPTOR-LOGGING - Remove HTTP request body logging, handle via request-response interceptor
    this.logger.d("Updating Template Subscription with body: ", body);
    return this.http.put(`${this.serviceUrl}/v1/classes/template-subscription/${subscriptionId}`, body, { headers });
  }


  public getAdhocParticipantListByClassId(classId: string): Observable<any> {
    return this.http.get(`${this.serviceUrl}/v1/classes/adhoc-participants/${classId}`);
  }

  /**
   * Returns enriched participant details for a class: loginId, isAdhoc, hasKey,
   * alreadyRedeemed, redemptionDate, redeemedByEmail, crossAccountRedemption, userName.
   * Response is wrapped: `{ participants, reason, message }`. When `reason === 'CLASS_NOT_CONFIRMED'`
   * the class has no keys yet and participants should not be shown.
   * Maps to GET /api/v1/classes/{classId}/participant-details.
   */
  public getParticipantDetails(classId: string): Observable<{ participants: any[]; reason: string | null; message: string | null }> {
    return this.http.get<{ participants: any[]; reason: string | null; message: string | null }>(
      `${this.serviceUrl}/v1/classes/${classId}/participant-details`
    );
  }


  public getParticipantListByClassId(classId: string): Observable<any> {
    return this.http.get(`${this.serviceUrl}/v1/classes/participants/${classId}`);
  }

  public getCloudUserAccountForTemplate(classId: string): Observable<any> {
    return this.http.get(`${this.serviceUrl}/v1/classes/cloudUserAccountForTemplate/${classId}`);
  }


  /**
   * Checks whether the specified user has an active Guacamole session in the specified class.
   * Used by the unified launcher to perform a final backend check before assembling a new
   * connection token, preventing cross-browser / incognito multi-session launches.
   */
  public checkSessionStatus(classId: string, userId: string): Observable<{ isActive: boolean; context: string | null }> {
    return this.http.get<{ isActive: boolean; context: string | null }>(
      `${this.serviceUrl}/v1/classes/session-status/${classId}/${userId}`
    );
  }

  public destroyOrgTemplate(templateId: string): Observable<any> {
    return this.http.delete(`${this.serviceUrl}/v1/classes/destroy-org-template/${templateId}`);
  }

  /** Powers on a deallocated or stopped org-template VM. */
  public startOrgTemplateVm(templateId: string): Observable<any> {
    return this.http.post(`${this.serviceUrl}/v1/classes/org-template/${templateId}/start`, {});
  }

  /** Sends a graceful ACPI shutdown signal to the org-template VM guest OS. */
  public shutdownOrgTemplateVm(templateId: string): Observable<any> {
    return this.http.post(`${this.serviceUrl}/v1/classes/org-template/${templateId}/shutdown`, {});
  }

  /** Gracefully shuts down the org-template VM guest OS then deallocates (Azure Portal "Stop"). */
  public stopOrgTemplateVm(templateId: string): Observable<any> {
    return this.http.post(`${this.serviceUrl}/v1/classes/org-template/${templateId}/stop`, {});
  }

  /** Returns the current Azure VM power state for an org-template VM. */
  public getOrgTemplateVmPowerState(templateId: string): Observable<{ powerState: string; templateId: string }> {
    return this.http.get<{ powerState: string; templateId: string }>(`${this.serviceUrl}/v1/classes/org-template/${templateId}/vm-power-state`);
  }

  /** Powers on a deallocated or stopped class-template VM using the customer's Azure credentials. */
  public startClassTemplateVm(resourceId: string, classId: string): Observable<any> {
    return this.http.post(`${this.serviceUrl}/v1/classes/class-template/${resourceId}/start/${classId}`, {});
  }

  /** Sends a graceful ACPI shutdown signal to a class-template VM guest OS. */
  public shutdownClassTemplateVm(resourceId: string, classId: string): Observable<any> {
    return this.http.post(`${this.serviceUrl}/v1/classes/class-template/${resourceId}/shutdown/${classId}`, {});
  }

  /** Force-deallocates a class-template VM with no OS signal. Compute released; charges paused. */
  public deallocateClassTemplateVm(resourceId: string, classId: string): Observable<any> {
    return this.http.post(`${this.serviceUrl}/v1/classes/class-template/${resourceId}/deallocate/${classId}`, {});
  }

  /** Gracefully shuts down then deallocates a class-template VM (Azure Portal "Stop"). */
  public stopClassTemplateVm(resourceId: string, classId: string): Observable<any> {
    return this.http.post(`${this.serviceUrl}/v1/classes/class-template/${resourceId}/stop/${classId}`, {});
  }

  public destroyTemplateUserAccount(templateId: string, classId: string): Observable<any> {
    return this.http.delete(`${this.serviceUrl}/v1/classes/destroy-template-user-account/${classId}`);
  }

  /**
   * Adds a single Azure subscription to a class's VMSubscription list.
   * The backend resolves the location from the class's template, auto-fills the display name,
   * and runs a live quota check as a side-effect.
   */
  public addClassVmSubscription(classId: string, subscriptionId: string, displayName = ''): Observable<any> {
    return this.http.post(
      `${this.serviceUrl}/v1/classes/${encodeURIComponent(classId)}/vm-subscription`,
      { subscriptionId, displayName }
    );
  }

  /** Removes a single VM subscription from a class by Azure subscription ID. */
  public removeClassVmSubscription(classId: string, subscriptionId: string): Observable<any> {
    return this.http.delete(
      `${this.serviceUrl}/v1/classes/${encodeURIComponent(classId)}/vm-subscription/${encodeURIComponent(subscriptionId)}`
    );
  }

  /** Triggers a live Azure quota refresh for a class VM subscription and returns updated quota data. */
  public refreshClassVmSubscriptionQuota(classId: string, subscriptionId: string): Observable<any> {
    return this.http.post(
      `${this.serviceUrl}/v1/classes/${encodeURIComponent(classId)}/vm-subscription/${encodeURIComponent(subscriptionId)}/refresh-quota`,
      {}
    );
  }

  // ---- VM Template (class-scoped) ----

  /**
   * Returns the current VM template lifecycle state for a class by reading
   * ResourceModelV2.CustomerStatuses from the backend (single source of truth).
   * The SNAPSHOTTED state is driven by the API returning status='completed', which the
   * backend liveness probe sets only when the Azure snapshot actually exists. The class stage
   * has no role here: a class may stay 'confirm' even after its template resources are gone.
   */
  public getVmTemplateStatus(templateId: string, customerId: string, classStage?: string, classId?: string): Observable<{ state: string; logs?: string[] }> {
    return this.getTemplateStatus(templateId, customerId, classId).pipe(
      map(res => {
        const status = (res?.status ?? 'draft').toLowerCase();
        const stateMap: Record<string, string> = {
          'draft': 'NOT_STARTED',
          'creating': 'IN_PROGRESS',
          'running': 'CREATED',
          'stopped': 'CREATED',
          'deallocated': 'CREATED',
          'destroying': 'IN_PROGRESS',
          'failed': 'FAILED',
          'active': 'CREATED',
          'snapshotting': 'SNAPSHOTTING',
          'snapshot_failed': 'FAILED',
          // 'completed' means the Azure snapshot exists and is confirmed ready.
          'completed': 'SNAPSHOTTED',
        };
        // If the backend confirms a snapshot record exists in CustomerCloudIds, always surface
        // SNAPSHOTTED regardless of the current VM power state stored in CustomerStatuses.
        const mappedState = stateMap[status] ?? 'NOT_STARTED';
        const state = res.isSnapshotExist ? 'SNAPSHOTTED' : mappedState;
        return { state };
      })
    );
  }

  /**
   * Triggers provisioning of the template VM for a class.
   * Maps to the existing GET /api/v1/classes/create-template/{classId}.
   */
  public createVmTemplate(classId: string): Observable<void> {
    return this.http.get<void>(
      `${this.serviceUrl}/v1/classes/create-template/${encodeURIComponent(classId)}`
    );
  }

  /**
   * Creates a snapshot of the template VM.
   * Looks up the VM resource template ID from the class document, then calls
   * PUT /api/v1/classes/snapshot/{templateId}.
   */
  public snapshotVmTemplate(classId: string): Observable<void> {
    return this.getClassByClassId(classId).pipe(
      switchMap(cls => {
        const templates: any[] =
          (cls as any)?.trainingSchedule?.resourceTemplateIds ?? [];
        const vmTemplate = templates.find(
          (r: any) =>
            (r.resourceType ?? r.ResourceType ?? '').toLowerCase() === 'vm'
        );
        const templateId: string | undefined = vmTemplate?.id ?? vmTemplate?.Id;

        if (!templateId) {
          throw new Error(`No VM resource template ID found for class ${classId}`);
        }

        return this.http.put<void>(
          `${this.serviceUrl}/v1/classes/snapshot/${encodeURIComponent(templateId)}`,
          {}
        );
      })
    );
  }

  /**
   * Atomically confirms a class via a single backend call:
   * snapshot (idempotent) → key generation (idempotent) → Stage update → requisition status.
   * Eliminates the two-call race condition of the old flow.
   */
  public confirmClass(classId: string, updatedBy: string): Observable<void> {
    const headers = { 'Content-Type': 'application/json' };
    return this.http.put<void>(
      `${this.serviceUrl}/v1/classes/${encodeURIComponent(classId)}/confirm`,
      { updatedBy },
      { headers }
    );
  }

  /**
   * Cancels an in-progress VM template provisioning job.
   * The backend runs provisioning fire-and-forget with no cancel endpoint, so
   * this is a soft client-side reset that resolves immediately.
   */
  public cancelVmTemplate(_classId: string): Observable<void> {
    return of(undefined as void);
  }

  /**
   * Prepares an encrypted Guacamole connection token for an org-template session.
   * Calls the deduplication-aware endpoint that prevents a single user from opening
   * multiple simultaneous connections to the same template VM.
   */
  public prepareLaunchContext(payload: {
    clientSha: string;
    context: string;
    userId: string;
    templateId?: string | null;
    classId?: string | null;
    studentUserId?: string | null;
    vmId?: string | null;
    userName: string;
    /**
     * Optional per-class VM Connection Template ID. When supplied, the backend overlays the
     * template's Allow* flags onto the global appsettings.json defaults inside the encrypted
     * Guacamole token. Omitted (or null) preserves the pre-template behaviour exactly.
     */
    vmConnectionTemplateId?: string | null;
  }): Observable<any> {
    return this.http.post(`${this.serviceUrl}/v1/launch/prepare`, payload);
  }

  /**
   * Assigns a VM Connection Template to a class by patching the class document's
   * `vmConnectionTemplateId` field. Pass `null` to clear the assignment and revert to the
   * global appsettings.json defaults at next launch.
   */
  public assignVmConnectionTemplate(classId: string, templateId: string | null): Observable<any> {
    return this.http.patch(
      `${this.serviceUrl}/v1/classes/${encodeURIComponent(classId)}/vm-connection-template`,
      { vmConnectionTemplateId: templateId },
    );
  }

  /**
   * Triggers two-phase participant VM provisioning.
   * The backend upserts userVmResources to state=creating and starts a
   * background Azure provisioning task.  Returns 202 immediately.
   */
  public prepareUserVm(classId: string): Observable<UserVmStatusResponse> {
    return this.http.post<UserVmStatusResponse>(
      `${this.serviceUrl}/v1/user-vm/prepare`,
      { classId }
    );
  }

  /**
   * Polls the current provisioning state for the caller's participant VM.
   * Maps HTTP 404 to { state: 'not_created' } so the frontend can handle
   * the pre-provision state without an error path.
   */
  public getUserVmStatus(classId: string): Observable<UserVmStatusResponse> {
    return this.http.get<UserVmStatusResponse>(
      `${this.serviceUrl}/v1/user-vm/status/${encodeURIComponent(classId)}`
    ).pipe(
      catchError(err =>
        err.status === 404
          ? of({ state: 'not_created' as UserVmProvisionState })
          : throwError(() => err)
      )
    );
  }

  // ---- ACC Subscriptions (class-scoped) ----

  /**
   * Adds a single Azure subscription to a class's ACC template subscription list.
   * The backend auto-names the subscription from the tenant registry.
   */
  public addClassAccSubscription(classId: string, subscriptionId: string): Observable<any> {
    return this.http.post(
      `${this.serviceUrl}/v1/classes/${encodeURIComponent(classId)}/acc-subscription`,
      { subscriptionId }
    );
  }

  /** Removes an ACC subscription from a class by Azure subscription ID. */
  public removeClassAccSubscription(classId: string, subscriptionId: string): Observable<any> {
    return this.http.delete(
      `${this.serviceUrl}/v1/classes/${encodeURIComponent(classId)}/acc-subscription/${encodeURIComponent(subscriptionId)}`
    );
  }

  // ---- Multi-Template Testing ----

  /**
   * Signals that a resource template is ready for testing.
   * Backend processes the test in the background and returns 202 immediately.
   * Call getTemplateTestingStatus to poll for completion.
   */
  public markTemplateTested(classId: string, templateId: string, templateType: string): Observable<any> {
    const updatedBy = localStorage.getItem('userId') ?? '';
    return this.http.put(
      `${this.serviceUrl}/v1/classes/${encodeURIComponent(classId)}/templates/${encodeURIComponent(templateId)}/mark-tested`,
      { templateType, updatedBy }
    );
  }

  /**
   * Returns the current testing status for a resource template in a class.
   * Status values: draft | testing | tested | failed
   */
  public getTemplateTestingStatus(classId: string, templateId: string): Observable<{ status: string; templateType: string; details?: string }> {
    return this.http.get<{ status: string; templateType: string; details?: string }>(
      `${this.serviceUrl}/v1/classes/${encodeURIComponent(classId)}/templates/${encodeURIComponent(templateId)}/testing-status`
    );
  }

  /**
   * Archives a class (soft-delete). Sets `IsArchived = true` on the server.
   * This is a prerequisite before archiving the parent requisition or any linked template.
   */
  archiveClass(classId: string): Observable<void> {
    return this.http.patch<void>(
      `${this.serviceUrl}/v1/classes/${encodeURIComponent(classId)}/archive`,
      null
    );
  }

  /**
   * Assigns an existing TenantSecret to a class, overriding the global customer-level
   * credential with a class-specific service principal.
   *
   * @param classId - The class MongoDB ObjectId string.
   * @param tenantSecretId - The TenantSecret MongoDB ObjectId string to assign.
   */
  public assignTenantSecret(classId: string, tenantSecretId: string): Observable<any> {
    return this.http.patch(
      `${this.serviceUrl}/v1/classes/by-class-id/${encodeURIComponent(classId)}/tenant-secret`,
      { tenantSecretId }
    );
  }
}
